import { Editor } from '@tiptap/react'
import { MiniTopic, MiniTopicType, StepDTO, TopicHierarchy } from '@deepguide-ai/types'
import { atom } from 'jotai'

// Core topic data atoms
export const topicsHierarchyAtom = atom<TopicHierarchy>({
  topics: [],
})

export const isLoadingTopicsAtom = atom<boolean>(false)

export const selectedTopicIdAtom = atom<string | null>(null)

export const stepsAtom = atom<StepDTO[]>([])

export const isLoadingStepsAtom = atom<boolean>(false)

// Dialog state atoms
export const isAddDialogOpenAtom = atom<boolean>(false)

// Template state atom for passing template ID to the dialog
export const selectedTemplateIdAtom = atom<string | null>(null)

export const isEditDialogOpenAtom = atom<boolean>(false)

export const isDeleteDialogOpenAtom = atom<boolean>(false)

export const isMoveDialogOpenAtom = atom<boolean>(false)

// Current topic state atoms
export const currentParentTopicAtom = atom<MiniTopic | null>(null)

export const currentSectionIdAtom = atom<string | null>(null)

export const currentTopicAtom = atom<MiniTopic | null>(null)

// Selected section state atom (for highlighting in SectionTree)
export const selectedSectionIdAtom = atom<string | null>(null)

export const currentTopicFathersAtom = atom<MiniTopic[]>([])

// Form state atoms
export const newTopicTitleAtom = atom<string>('')

export const newTopicDescriptionAtom = atom<string>('')

export const newTopicTypeAtom = atom<MiniTopicType>(MiniTopicType.GUIDE)

// Save state atom
export const lastSavedAtAtom = atom<string | null>(null)

// Generation state atoms that map to useTopicGeneration hook
export const isGenerationModeAtom = atom<boolean>(false)

export const generationStatusAtom = atom<
  | 'not_started'
  | 'topic_generation'
  | 'article_generation'
  | 'completed'
  | 'error'
  | 'thinking'
>('not_started')

export const isMockModeAtom = atom<boolean>(false)

// Derived atoms based on generation state
export const isStreamingAtom = atom<boolean>((get) => {
  const isGenerationMode = get(isGenerationModeAtom)
  const generationStatus = get(generationStatusAtom)
  return (
    isGenerationMode &&
    (generationStatus === 'topic_generation' ||
      generationStatus === 'article_generation')
  )
})

export const isCreatingTopicAtom = atom<boolean>((get) => {
  const isGenerationMode = get(isGenerationModeAtom)
  const generationStatus = get(generationStatusAtom)
  return isGenerationMode && generationStatus === 'topic_generation'
})

export const isGeneratingArticleAtom = atom<boolean>((get) => {
  const isGenerationMode = get(isGenerationModeAtom)
  const generationStatus = get(generationStatusAtom)
  return isGenerationMode && generationStatus === 'article_generation'
})

// Legacy atoms - keeping for backward compatibility
export const isLiveModeAtom = atom<boolean>(false)

// Editor ref atom
export const editorRefAtom = atom<Editor | null>(null)

// Derived atoms
export const allTopicsFlatAtom = atom<MiniTopic[]>((get) => {
  const hierarchy = get(topicsHierarchyAtom)
  const flattenTopics = (topics: MiniTopic[]): MiniTopic[] => {
    const result: MiniTopic[] = []
    for (const topic of topics) {
      result.push(topic)
      if (topic.subtopics && topic.subtopics.length > 0) {
        result.push(...flattenTopics(topic.subtopics))
      }
    }
    return result
  }
  return flattenTopics(hierarchy.topics)
})

export const selectedTopicAtom = atom<MiniTopic | null>((get) => {
  const selectedId = get(selectedTopicIdAtom)
  const allTopics = get(allTopicsFlatAtom)
  return allTopics.find((topic) => topic.id === selectedId) ?? null
})

export const topicTreeAtom = atom<MiniTopic[]>((get) => {
  const allTopics = get(allTopicsFlatAtom)
  // add root topic to the tree
  const rootTopic: MiniTopic = {
    id: '__root__',
    title: 'Root',
    type: 'folder' as const,
    description: 'Root topic',
    subtopics: allTopics.filter((topic) => topic.parentId === null),
  }
  return [rootTopic, ...allTopics]
})

// Action atoms (write-only atoms for complex operations)
export const closeAllDialogsAtom = atom(null, (_get, set) => {
  set(isAddDialogOpenAtom, false)
  set(isEditDialogOpenAtom, false)
  set(isDeleteDialogOpenAtom, false)
  set(isMoveDialogOpenAtom, false)
  set(selectedTemplateIdAtom, null)
  set(newTopicTypeAtom, MiniTopicType.GUIDE)
})

export const openAddDialogAtom = atom(
  null,
  (_get, set, parentTopic: MiniTopic | null, sectionId?: string, templateId?: string | null, topicType: MiniTopicType = MiniTopicType.GUIDE) => {
    set(currentParentTopicAtom, parentTopic)
    set(currentSectionIdAtom, sectionId || null)
    set(selectedTemplateIdAtom, templateId || null)
    set(newTopicTypeAtom, topicType)
    set(isAddDialogOpenAtom, true)
  }
)

export const openEditDialogAtom = atom(null, (_get, set, topic: MiniTopic) => {
  set(currentTopicAtom, topic)
  set(newTopicTitleAtom, topic.title)
  set(newTopicDescriptionAtom, topic.description || '')
  set(isEditDialogOpenAtom, true)
})

export const openDeleteDialogAtom = atom(
  null,
  (_get, set, topic: MiniTopic) => {
    set(currentTopicAtom, topic)
    set(isDeleteDialogOpenAtom, true)
  }
)

export const openMoveDialogAtom = atom(null, (_get, set, topic: MiniTopic) => {
  set(currentTopicAtom, topic)
  set(isMoveDialogOpenAtom, true)
})

// Topics count utility atom
export const topicsCountAtom = atom<(rootId?: string) => number>((get) => {
  const hierarchy = get(topicsHierarchyAtom)

  const getTopicsCount = (topics: MiniTopic[], rootId?: string): number => {
    if (!rootId) {
      return topics.length
    }

    const countTopicsRecursively = (topicList: MiniTopic[]): number => {
      let count = 0
      for (const topic of topicList) {
        count++
        if (topic.subtopics && topic.subtopics.length > 0) {
          count += countTopicsRecursively(topic.subtopics)
        }
      }
      return count
    }

    const rootTopic = topics.find((t) => t.id === rootId)
    if (!rootTopic) return 0

    return rootTopic.subtopics ? countTopicsRecursively(rootTopic.subtopics) : 0
  }

  return (rootId?: string) => getTopicsCount(hierarchy.topics, rootId)
})

// Section-related derived atoms
// Get all sections (topics with type === 'SECTION')
export const sectionsAtom = atom<MiniTopic[]>((get) => {
  const hierarchy = get(topicsHierarchyAtom)
  return hierarchy.topics.filter((topic) => topic.type === 'SECTION')
})

// Get sections with their immediate children
export const sectionsWithChildrenAtom = atom<MiniTopic[]>((get) => {
  const sections = get(sectionsAtom)
  // Sections already have their subtopics populated
  return sections
})

// Get all sections flat (including nested sections if any)
export const allSectionsFlatAtom = atom<MiniTopic[]>((get) => {
  const hierarchy = get(topicsHierarchyAtom)
  const sections: MiniTopic[] = []
  
  const collectSections = (topics: MiniTopic[]) => {
    topics.forEach(topic => {
      if (topic.type === 'SECTION') {
        sections.push(topic)
      }
      // Recurse through subtopics to find nested sections
      if (topic.subtopics?.length) {
        collectSections(topic.subtopics)
      }
    })
  }
  
  collectSections(hierarchy.topics)
  return sections
})

// Get children of a specific section
export const sectionChildrenAtom = atom<MiniTopic[]>((get) => {
  const currentSectionId = get(currentSectionIdAtom)
  const sections = get(sectionsAtom)
  
  if (!currentSectionId) return []
  
  const section = sections.find(s => s.id === currentSectionId)
  return section?.subtopics || []
})

// Get sections for current topic (if topic is under a section)
export const sectionsForCurrentTopicAtom = atom<MiniTopic | null>((get) => {
  const currentTopic = get(currentTopicAtom)
  const sections = get(sectionsAtom)

  if (!currentTopic?.parentId) return null

  // Find if parent is a section
  return sections.find(s => s.id === currentTopic.parentId) || null
})

// Get default selected section (first section if none selected, or explicitly selected section)
export const defaultSelectedSectionAtom = atom<MiniTopic | null>((get) => {
  const selectedSectionId = get(selectedSectionIdAtom)
  const sections = get(sectionsAtom)

  if (!sections || sections.length === 0) return null

  // If a section is explicitly selected, return it
  if (selectedSectionId) {
    const found = sections.find(s => s.id === selectedSectionId)
    if (found) return found
  }

  // Otherwise, return first section as default
  return sections[0]
})
